import React, { useState, useEffect } from 'react';
import { roleService } from '../../../services/roleService';
import { menuService } from '../../../services/menuService';
import { useAuth } from '../../Admin/context/AuthContext';   // ✅ sesuaikan path
import {
    FaPlus, FaEdit, FaTrash, FaSearch, FaShieldAlt,
    FaTimes, FaSave, FaCheckCircle, FaExclamationTriangle,
    FaKey, FaUsers, FaLock, FaUnlock, FaChevronDown, FaChevronUp,
    FaFolder, FaFolderOpen, FaFile, FaSitemap
} from 'react-icons/fa';

const ManajemenRole = () => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [expandedRole, setExpandedRole] = useState(null);
    const [expandedModules, setExpandedModules] = useState([]);

    const [formData, setFormData] = useState({
        name: '',
        guard_name: 'web',
        permissions: []
    });

    const [errors, setErrors] = useState({});

    // ✅ Ambil user dari AuthContext
    const { user } = useAuth();

    // ✅ Helper cek permission
    const hasPermission = (permission) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    };

    // Load roles
    const loadRoles = async () => {
        setLoading(true);
        const result = await roleService.getRoles();
        if (result.success) {
            setRoles(result.data || []);
        } else {
            showNotification(result.message, 'error');
        }
        setLoading(false);
    };

    // Load permissions
    const loadPermissions = async () => {
        const result = await roleService.getPermissions();
        if (result.success) {
            setPermissions(result.data || []);
        }
    };

    // Load menus
    const loadMenus = async () => {
        try {
            const result = await menuService.getMenus();
            if (result.success) {
                setMenus(result.data || []);
            }
        } catch (error) {
            console.error('Gagal load menus:', error);
        }
    };

    useEffect(() => {
        loadRoles();
        loadPermissions();
        loadMenus();
    }, []);

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const filteredRoles = roles.filter(role =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Group permissions by menu
    const groupPermissionsByMenu = () => {
        const menuMap = {};
        menus.forEach(menu => {
            const routePrefix = menu.route
                ?.replace('/admin/', '')
                ?.split('/')[0]
                ?.toLowerCase();
            if (routePrefix) {
                menuMap[routePrefix] = { menu, prefix: routePrefix };
            }
        });

        const grouped = {};

        permissions.forEach(perm => {
            const prefix = perm.name.split('.')[0]?.toLowerCase() || 'other';
            const menuInfo = menuMap[prefix];
            const groupKey = prefix;
            const groupLabel = menuInfo?.menu?.name || formatGroupLabel(prefix);

            if (!grouped[groupKey]) {
                grouped[groupKey] = {
                    key: groupKey,
                    label: groupLabel,
                    icon: menuInfo?.menu?.icon || 'file',
                    menuId: menuInfo?.menu?.id || null,
                    permissions: [],
                };
            }
            grouped[groupKey].permissions.push(perm);
        });

        return Object.values(grouped).sort((a, b) => {
            const orderA = a.menuId ? menus.find(m => m.id === a.menuId)?.order : 999;
            const orderB = b.menuId ? menus.find(m => m.id === b.menuId)?.order : 999;
            return (orderA || 999) - (orderB || 999);
        });
    };

    const formatGroupLabel = (key) => {
        const labels = {
            'dashboard': 'Dashboard',
            'user': 'Manajemen User',
            'users': 'Manajemen User',
            'role': 'Manajemen Role',
            'roles': 'Manajemen Role',
            'menu': 'Manajemen Menu',
            'menus': 'Manajemen Menu',
            'artikel': 'Manajemen Artikel',
            'apbd': 'Manajemen APBD',
            'profil': 'Manajemen Profil',
            'profile': 'Manajemen Profil',
            'kontak': 'Kontak',
            'agenda': 'Agenda',
            'slider': 'Slider',
            'sambutan': 'Sambutan',
            'produk-hukum': 'Produk Hukum',
            'frontend-menu': 'Frontend Menu',
        };
        return labels[key] || key.charAt(0).toUpperCase() + key.slice(1);
    };

    const groupedPermissions = groupPermissionsByMenu();

    const handleCreate = () => {
        setEditingRole(null);
        setFormData({
            name: '',
            guard_name: 'web',
            permissions: []
        });
        setErrors({});
        setExpandedModules([]);
        setShowForm(true);
    };

    const handleEdit = (role) => {
        setEditingRole(role);
        setFormData({
            name: role.name || '',
            guard_name: role.guard_name || 'web',
            permissions: role.permissions ? role.permissions.map(p => p.id) : []
        });
        setErrors({});
        const selectedIds = role.permissions?.map(p => p.id) || [];
        const expanded = groupedPermissions
            .filter(g => g.permissions.some(p => selectedIds.includes(p.id)))
            .map(g => g.key);
        setExpandedModules(expanded);
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handlePermissionChange = (permissionId) => {
        setFormData(prev => {
            const permissions = prev.permissions.includes(permissionId)
                ? prev.permissions.filter(id => id !== permissionId)
                : [...prev.permissions, permissionId];
            return { ...prev, permissions };
        });
    };

    const handleSelectGroup = (groupKey) => {
        const group = groupedPermissions.find(g => g.key === groupKey);
        if (!group) return;
        const groupIds = group.permissions.map(p => p.id);

        setFormData(prev => {
            const allSelected = groupIds.every(id => prev.permissions.includes(id));
            let newPermissions;
            if (allSelected) {
                newPermissions = prev.permissions.filter(id => !groupIds.includes(id));
            } else {
                newPermissions = [...new Set([...prev.permissions, ...groupIds])];
            }
            return { ...prev, permissions: newPermissions };
        });
    };

    const handleSelectAll = () => {
        const allIds = permissions.map(p => p.id);
        setFormData(prev => {
            const allSelected = allIds.every(id => prev.permissions.includes(id));
            return {
                ...prev,
                permissions: allSelected ? [] : allIds
            };
        });
    };

    const toggleModule = (key) => {
        setExpandedModules(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Nama role wajib diisi';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSubmitting(true);

        let result;
        if (editingRole) {
            result = await roleService.updateRole(editingRole.id, formData);
        } else {
            result = await roleService.createRole(formData);
        }

        if (result.success) {
            showNotification(result.message || 'Role berhasil disimpan', 'success');
            setShowForm(false);
            loadRoles();
        } else {
            showNotification(result.message || 'Gagal menyimpan role', 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = (role) => setDeleteConfirm(role);

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        const result = await roleService.deleteRole(deleteConfirm.id);
        if (result.success) {
            showNotification('Role berhasil dihapus', 'success');
            loadRoles();
        } else {
            showNotification(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const toggleExpand = (roleId) => {
        setExpandedRole(expandedRole === roleId ? null : roleId);
    };

    const getPermissionCount = (role) => role.permissions ? role.permissions.length : 0;

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Role</h2>
                    <p className="text-gray-600">Kelola role dan hak akses pengguna</p>
                </div>
                {/* ✅ Tombol Tambah Role — cek permission */}
                {hasPermission('roles.create') && (
                    <button
                        onClick={handleCreate}
                        className="btn-primary flex items-center justify-center whitespace-nowrap"
                    >
                        <FaPlus className="mr-2" />
                        Tambah Role
                    </button>
                )}
            </div>

            {/* Notification */}
            {notification && (
                <div className={`mb-6 p-4 rounded-lg flex items-center ${notification.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success' ? (
                        <FaCheckCircle className="mr-3 flex-shrink-0" />
                    ) : (
                        <FaExclamationTriangle className="mr-3 flex-shrink-0" />
                    )}
                    {notification.message}
                </div>
            )}

            {/* Search Bar */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari role..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                    />
                </div>
            </div>

            {/* Roles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    <div className="col-span-full text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                    </div>
                ) : filteredRoles.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-500">
                        Tidak ada data role
                    </div>
                ) : (
                    filteredRoles.map((role) => (
                        <div key={role.id} className="card-glass overflow-hidden">
                            <div className="bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] p-4 text-white">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                                            <FaShieldAlt className="text-xl" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-lg capitalize">{role.name}</h3>
                                            <p className="text-xs opacity-80">Guard: {role.guard_name}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => toggleExpand(role.id)}
                                        className="text-white/80 hover:text-white"
                                    >
                                        {expandedRole === role.id ? <FaChevronUp /> : <FaChevronDown />}
                                    </button>
                                </div>
                            </div>

                            <div className="p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center text-sm text-gray-600">
                                        <FaKey className="mr-2 text-[#2E7D32]" />
                                        {getPermissionCount(role)} permissions
                                    </div>
                                </div>

                                {expandedRole === role.id && (
                                    <div className="mb-4">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Permissions:</h4>
                                        <div className="flex flex-wrap gap-1">
                                            {role.permissions && role.permissions.length > 0 ? (
                                                role.permissions.map((perm) => (
                                                    <span
                                                        key={perm.id}
                                                        className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full"
                                                    >
                                                        {perm.name}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-gray-400 text-sm">Tidak ada permission</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end space-x-2 pt-3 border-t">
                                    {/* ✅ Tombol Edit — cek permission */}
                                    {hasPermission('roles.update') && (
                                        <button
                                            onClick={() => handleEdit(role)}
                                            className="flex items-center px-3 py-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors text-sm"
                                        >
                                            <FaEdit className="mr-1" /> Edit
                                        </button>
                                    )}

                                    {/* ✅ Tombol Hapus — cek permission */}
                                    {hasPermission('roles.delete') && (
                                        <button
                                            onClick={() => handleDelete(role)}
                                            className="flex items-center px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm"
                                        >
                                            <FaTrash className="mr-1" /> Hapus
                                        </button>
                                    )}

                                    {/* Fallback jika tidak ada permission */}
                                    {!hasPermission('roles.update') && !hasPermission('roles.delete') && (
                                        <span className="text-xs text-gray-400 italic">-</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A] flex items-center">
                                    <FaShieldAlt className="mr-2 text-[#2E7D32]" />
                                    {editingRole ? 'Edit Role' : 'Tambah Role Baru'}
                                </h3>
                                <button
                                    onClick={() => setShowForm(false)}
                                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full"
                                >
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nama Role <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                                }`}
                                            placeholder="Contoh: admin, editor, viewer"
                                        />
                                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Guard Name
                                        </label>
                                        <select
                                            name="guard_name"
                                            value={formData.guard_name}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                        >
                                            <option value="web">web</option>
                                            <option value="api">api</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Permissions — Group by Menu */}
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="block text-sm font-medium text-gray-700 flex items-center">
                                            <FaSitemap className="mr-2 text-[#2E7D32]" />
                                            Permissions ({formData.permissions.length} dipilih)
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleSelectAll}
                                            className="text-sm text-[#2E7D32] hover:text-[#4CAF50] font-medium"
                                        >
                                            {formData.permissions.length === permissions.length
                                                ? 'Unselect All'
                                                : 'Select All'}
                                        </button>
                                    </div>

                                    <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                                        {groupedPermissions.length === 0 ? (
                                            <p className="text-center text-gray-400 py-6 text-sm">
                                                Belum ada permission
                                            </p>
                                        ) : (
                                            groupedPermissions.map((group) => {
                                                const selectedInGroup = group.permissions.filter(p =>
                                                    formData.permissions.includes(p.id)
                                                ).length;
                                                const isExpanded = expandedModules.includes(group.key);
                                                const allSelected = selectedInGroup === group.permissions.length;
                                                const someSelected = selectedInGroup > 0 && !allSelected;

                                                return (
                                                    <div
                                                        key={group.key}
                                                        className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                                                    >
                                                        <div className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors">
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleModule(group.key)}
                                                                className="flex items-center flex-1 text-left"
                                                            >
                                                                {isExpanded ? (
                                                                    <FaFolderOpen className="text-[#2E7D32] mr-3 flex-shrink-0" />
                                                                ) : (
                                                                    <FaFolder className="text-[#2E7D32] mr-3 flex-shrink-0" />
                                                                )}
                                                                <span className="font-semibold text-sm text-gray-700">
                                                                    {group.label}
                                                                </span>
                                                                <span className="ml-2 text-xs text-gray-500">
                                                                    ({selectedInGroup}/{group.permissions.length})
                                                                </span>
                                                            </button>

                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSelectGroup(group.key)}
                                                                    className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${allSelected
                                                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                                        : someSelected
                                                                            ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                        }`}
                                                                >
                                                                    {allSelected ? 'Unselect' : 'Select All'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleModule(group.key)}
                                                                    className="text-gray-400 hover:text-gray-600 p-1"
                                                                >
                                                                    {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {isExpanded && (
                                                            <div className="border-t bg-gray-50 p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                {group.permissions.map((perm) => (
                                                                    <label
                                                                        key={perm.id}
                                                                        className="flex items-center p-2 bg-white rounded cursor-pointer hover:bg-green-50 transition-colors border border-gray-100"
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={formData.permissions.includes(perm.id)}
                                                                            onChange={() => handlePermissionChange(perm.id)}
                                                                            className="mr-2 text-[#2E7D32] focus:ring-[#2E7D32] rounded"
                                                                        />
                                                                        <span className="text-xs text-gray-700">
                                                                            {perm.name.split('.').slice(1).join('.') || perm.name}
                                                                        </span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-3 pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all ${submitting ? 'opacity-75 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Menyimpan...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editingRole ? 'Update Role' : 'Simpan Role'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaTrash className="text-3xl text-red-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Hapus Role?</h3>
                            <p className="text-gray-600 mb-6">
                                Anda akan menghapus role <strong className="capitalize">{deleteConfirm.name}</strong>.
                                User dengan role ini akan kehilangan akses.
                            </p>
                            <div className="flex justify-center space-x-3">
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Batal
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-6 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
                                >
                                    Hapus
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenRole;